import { resolveCanvasTimePage } from "@/components/canvas/canvas-time-pages";
import { applyCanvasTimePageLayout } from "@/lib/canvas-time-page-layout";
import { rememberCanvasTimePageMembership } from "@/lib/canvas-time-page-membership";
import { persistFrameGeometry } from "@/lib/frame-geometry-persistence";
import { useCanvasStore } from "@/stores/canvas-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { useCanvasTimePageStore } from "@/stores/canvas-time-page-store";
import { rememberLocalFrameGeometry } from "@/stores/frame-geometry-guard";
import { useProjectStore } from "@/stores/project-store";
import { setFrameHidden } from "@/lib/frame-visibility";

interface FitCanvasTimePageOptions {
  /** Restore the page grid with current frame dimensions before fitting. */
  tidyFrameLayout?: boolean;
}

function tidySpaceFrames(projectId?: string, spaceId?: string): void {
  const canvas = useCanvasStore.getState();
  const layout = applyCanvasTimePageLayout(canvas.nodes, spaceId);
  rememberCanvasTimePageMembership(layout.nodes, spaceId);
  if (layout.changedUpdates.length === 0) return;
  for (const update of layout.changedUpdates) {
    const geometry = { position_x: update.x, position_y: update.y };
    rememberLocalFrameGeometry(update.id, geometry);
    if (projectId && spaceId) {
      void persistFrameGeometry(projectId, spaceId, update.id, geometry).catch(
        () => {
          // Keep the local arrangement usable if persistence is unavailable.
        },
      );
    }
  }
  canvas.setNodes(layout.nodes);
}

export function fitCanvasTimePage(
  requestedIndex?: number,
  options: FitCanvasTimePageOptions = {},
): boolean {
  const initialCanvas = useCanvasStore.getState();
  const pageState = useCanvasTimePageStore.getState();
  const projectState = useProjectStore.getState();
  const currentSpaceId = projectState.currentSpace?.id;
  if (!initialCanvas.fitViewNodes) return false;
  if (
    options.tidyFrameLayout &&
    !usePresentationStore.getState().presentation
  ) {
    pageState.setLayout(null);
    tidySpaceFrames(projectState.currentProject?.id, currentSpaceId);
  }
  const resolved = resolveCanvasTimePage(
    useCanvasStore.getState().nodes,
    requestedIndex ?? pageState.activePageIndex,
    currentSpaceId,
  );
  if (!resolved.page) return false;

  pageState.setActivePageIndex(resolved.index);
  initialCanvas.fitViewNodes(resolved.page.nodeIds);
  return true;
}

/**
 * Select every visible frame on a time page (bulk archive flow).
 * Fits the page first so the selection is visible, then marks all of
 * its frames selected and — when opened from a cursor-anchored menu —
 * pops the multi-selection menu (Hide/Delete) right at that anchor so
 * the follow-up actions are one click away.
 */
export function selectCanvasTimePage(
  requestedIndex: number,
  anchor?: { x: number; y: number },
): boolean {
  if (!fitCanvasTimePage(requestedIndex)) return false;
  const canvas = useCanvasStore.getState();
  const currentSpaceId = useProjectStore.getState().currentSpace?.id;
  const resolved = resolveCanvasTimePage(
    canvas.nodes,
    useCanvasTimePageStore.getState().activePageIndex,
    currentSpaceId,
  );
  if (!resolved.page || resolved.page.nodeIds.length === 0) return false;
  const first = resolved.page.nodeIds[0];
  if (!first) return false;
  if (resolved.page.nodeIds.length === 1) {
    canvas.selectFrame(first);
    return true;
  }
  canvas.selectFrames(resolved.page.nodeIds);
  if (anchor) canvas.openMultiSelectContextMenu(anchor.x, anchor.y);
  return true;
}

/**
 * Archive a whole time page in one click: hides every visible frame on
 * the page (durable `metadata.hidden`, still searchable, auto-unhides
 * on open) and falls back to Recent when the active page is gone.
 */
export function hideCanvasTimePage(requestedIndex: number): boolean {
  const canvas = useCanvasStore.getState();
  const pageState = useCanvasTimePageStore.getState();
  const currentSpaceId = useProjectStore.getState().currentSpace?.id;
  const previousActiveId =
    resolveCanvasTimePage(
      canvas.nodes,
      pageState.activePageIndex,
      currentSpaceId,
    ).page?.id ?? null;
  const resolved = resolveCanvasTimePage(
    canvas.nodes,
    requestedIndex,
    currentSpaceId,
  );
  if (!resolved.page || resolved.page.nodeIds.length === 0) return false;
  canvas.clearMultiSelect();
  for (const id of resolved.page.nodeIds) {
    setFrameHidden(id, true);
  }
  const next = useCanvasStore.getState();
  const pages = resolveCanvasTimePage(
    next.nodes,
    pageState.activePageIndex,
    currentSpaceId,
  ).pages;
  if (pages.length === 0) {
    pageState.setActivePageIndex(0);
    return true;
  }
  const stillThere = previousActiveId
    ? pages.findIndex((page) => page.id === previousActiveId)
    : -1;
  const nextIndex = stillThere >= 0 ? stillThere : 0;
  pageState.setActivePageIndex(nextIndex);
  if (stillThere < 0 && next.fitViewNodes) {
    next.fitViewNodes(pages[nextIndex]?.nodeIds ?? []);
  }
  return true;
}
